import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  ArrowRight,
  History,
  Plus,
} from "lucide-react";
import ConfirmationDialog from "@/components/ConfirmationDialog/ConfirmationDialog";
import DataTable from "@/components/Datatable/Datatable";
import { Column } from "@/components/Datatable/types";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Badge } from "@/components/Badge/Badge";
import { Button } from "@/components/ui/button";
import { useCompanyDiagnostics } from "../../hooks/useCompanyDiagnostics";
import { DownloadReportButton } from "../../pdf/DownloadReportButton";
import { headlineScore } from "../../utils/diagnosticScoring";
import { FALLBACK_TRADE_OPTIONS, tradeLabels } from "../../utils/trades";
import { NewDiagnosticDialog } from "../NewDiagnosticDialog/NewDiagnosticDialog";
import { DiagnosticLoadFailed } from "../DiagnosticLoadFailed";
import { DiagnosticNotFound } from "../DiagnosticNotFound";
import { DiagnosticCompanyDetailSkeleton } from "../DiagnosticSkeletons";
import { parseRecordId } from "../../utils/routeParams";
import { AuditTrail } from "./AuditTrail";
import type { BuildReportInput } from "../../pdf/diagnosticReportData";
import type { CompanyAttemptDetail } from "../../types/diagnostic";

/**
 * The stage to show for an attempt.
 *
 * A completed attempt shows the stage recorded when it was completed, never a
 * value recomputed on read: `trades` was added to the schema later and
 * defaults to `[]`, which makes every trade-tagged question inapplicable and
 * can silently re-place a historical attempt. The recomputed band is only a
 * fallback for drafts and for attempts completed before the column existed.
 */
export const displayStage = (attempt: CompanyAttemptDetail): string | null =>
  attempt.finalStageLabel ?? attempt.result.band?.label ?? null;

/**
 * The stored attempt as the report reads it.
 *
 * `recordedStageLabel` comes straight from `finalStageLabel` rather than from
 * `displayStage()`: the report has its own fallback to the recomputed band, and
 * passing the fallback through as if it were recorded would hide that a stage
 * was never stored. The subject is the company — a diagnostic has no other
 * subject.
 *
 * No `tradeOptions`: the company-detail payload does not serve
 * `availableTrades` (only the coach-attempt payload does), so the report falls
 * back to the shipped list.
 */
const reportInputFor = (
  attempt: CompanyAttemptDetail,
  companyName: string,
): BuildReportInput => ({
  diagnosticName: attempt.diagnosticName,
  versionNumber: attempt.diagnosticVersionNumber,
  subjectName: companyName,
  completedAt: attempt.completedAt,
  coachNotes: attempt.coachNotes,
  version: attempt.version,
  responses: attempt.responses,
  result: attempt.result,
  recordedStageLabel: attempt.finalStageLabel,
  // The same recorded outcome the Status column shows, so the artifact the
  // owner keeps cannot contradict the table it was downloaded from.
  recordedOutcome: attempt.finalOutcome,
  trades: attempt.trades,
});

export const CompanyDetail: React.FC = () => {
  const navigate = useNavigate();
  const { companyId: rawId } = useParams<{ companyId: string }>();
  // `null` for a junk segment. The hook refuses a non-finite id, so passing
  // `NaN` through left the page on a skeleton forever.
  const companyId = parseRecordId(rawId);
  const {
    detail,
    status,
    loading,
    notFound,
    archivingId,
    pagination,
    handlePageChange,
    refresh,
    setArchived,
    showArchived,
    setShowArchived,
  } = useCompanyDiagnostics(companyId ?? NaN);

  const [newOpen, setNewOpen] = useState(false);
  const [pendingArchive, setPendingArchive] =
    useState<CompanyAttemptDetail | null>(null);

  // One page of the history, not the whole of it. `totalCount` is the only
  // honest source for "how many diagnostics does this company have" — the row
  // count is capped at the page size, which is what made a company with more
  // than 25 attempts look like it had exactly 25.
  //
  // Both describe the same set, archived rows included or excluded together
  // (D27), so the rows are rendered as they arrive: filtering them here again
  // is what put a pager reading 30 over 10 rows.
  const attempts = detail?.attempts ?? [];
  const totalCount = detail?.totalCount ?? 0;
  const companyName = detail?.company?.name ?? "Company";

  const columns: Column<CompanyAttemptDetail>[] = [
    {
      id: "diagnosticName",
      header: "Diagnostic",
      cell: ({ row }) => (
        <div>
          <div className="text-gray-900 dark:text-white">
            {row.original.diagnosticName}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <span>v{row.original.diagnosticVersionNumber}</span>
            {/* Two placements are only comparable across the same trades. */}
            {row.original.trades.length > 0 && (
              <>
                <span>·</span>
                <span>
                  {tradeLabels(
                    row.original.trades,
                    FALLBACK_TRADE_OPTIONS,
                  ).join(", ")}
                </span>
              </>
            )}
            {row.original.editCount > 0 && (
              <>
                <span>·</span>
                <span className="inline-flex items-center gap-1">
                  <History className="h-3 w-3" />
                  edited {row.original.editCount}&times;
                </span>
              </>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "completedAt",
      header: "Completed",
      cell: ({ row }) => (
        <span className="text-gray-500 dark:text-gray-400">
          {row.original.completedAt
            ? new Date(row.original.completedAt).toLocaleDateString()
            : "—"}
        </span>
      ),
    },
    {
      id: "stage",
      header: "Stage",
      cell: ({ row }) => {
        const stage = displayStage(row.original);
        return stage ? (
          <Badge variant="grey">{stage}</Badge>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">—</span>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant="grey">{statusLabel(row.original)}</Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {/* D18. The way into an attempt from its company's history.
              Without it the correction and audit-trail flow was reachable
              only by typing a URL — the source has the same gap and this is
              a deliberate divergence from it.

              Withheld on an archived attempt: the API refuses saves,
              completions and edits on one, so a screen that still looks
              editable would only mislead. Restore, in this same cell, is the
              way back in.

              It lives in the actions cell for a reason — that is the only
              cell whose click does not toggle the row open (Datatable's
              `stopRowClick`). */}
          {!row.original.isArchived && (
            <Button asChild size="sm" variant="ghost">
              <Link to={`/admin/diagnostics/attempts/${row.original.id}`}>
                <ArrowRight aria-hidden="true" className="mr-1 h-4 w-4" />
                {row.original.status === "in_progress" ? "Continue" : "Open"}
              </Link>
            </Button>
          )}
          {/* Only a completed attempt has a stage to hand over. A draft's
              report is produced from the fill screen, where the coach can see
              exactly which answers it is built from. */}
          {row.original.status === "complete" && (
            <DownloadReportButton
              report={() => reportInputFor(row.original, companyName)}
            />
          )}
          <Button
            disabled={archivingId === row.original.id}
            onClick={() => setPendingArchive(row.original)}
            size="sm"
            variant="ghost"
          >
            {row.original.isArchived ? (
              <>
                <ArchiveRestore className="mr-1 h-4 w-4" />
                Restore
              </>
            ) : (
              <>
                <Archive className="mr-1 h-4 w-4" />
                Archive
              </>
            )}
          </Button>
        </div>
      ),
    },
  ];

  if (companyId === null || notFound) {
    // A junk segment, or a numeric id no company row answers to. Both are
    // "the address names nothing" — distinct from a company that exists and
    // has never been diagnosed, which the API answers 200 with a null company.
    return (
      <DiagnosticNotFound message="That is not a company we can look up." />
    );
  }

  // A load that failed. It must not fall through to the branches below —
  // `detail?.company === null` is `false` when `detail` is null (it is
  // `undefined`), so a failure used to render the *populated* page: this
  // company's name replaced by "Company", "0 diagnostics on record", and an
  // empty table reading "No diagnostics yet" over a history that may be full.
  // Round 4 stopped that by falling back to the skeleton, which fixed the lie
  // but left the page saying "still coming" about a request that had already
  // failed, for ever. Three states, three renders: this is the terminal one.
  if (status === "failed") {
    return (
      <MainPageWrapper title={companyName}>
        <Button
          onClick={() => navigate("/admin/diagnostics")}
          size="sm"
          variant="ghost"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to companies
        </Button>
        <div className="mt-6">
          <DiagnosticLoadFailed
            message="This company's diagnostics could not be loaded."
            onRetry={refresh}
          />
        </div>
      </MainPageWrapper>
    );
  }

  // Still in flight, with nothing yet to draw.
  if (!detail) {
    return <DiagnosticCompanyDetailSkeleton />;
  }

  return (
    <MainPageWrapper
      actions={
        <Button onClick={() => setNewOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          New Diagnostic
        </Button>
      }
      title={companyName}
    >
      <Button
        onClick={() => navigate("/admin/diagnostics")}
        size="sm"
        variant="ghost"
      >
        <ArrowLeft className="mr-1 h-4 w-4" />
        Back to companies
      </Button>

      {detail.company === null ? (
        // A company that exists but has never been diagnosed. Reachable by URL
        // guess and after discarding the last draft — the API answers with a
        // null company rather than a 404, and this is the screen for it.
        //
        // Keyed on `company`, never on an empty `attempts`: paging past the
        // end of a real history also returns no rows, and rendering *that* as
        // "no diagnostics on record" would tell a coach a company he just
        // diagnosed has never been assessed.
        <div className="mt-6 rounded-lg border border-gray-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-800">
          <p className="text-sm text-gray-900 dark:text-white">
            No diagnostics on record for this company.
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Use New Diagnostic to start one.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
            <div className="text-base text-gray-900 dark:text-white">
              {companyName}
            </div>
            <div className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {totalCount} diagnostic{totalCount === 1 ? "" : "s"} on record
            </div>
          </div>

          <div className="mb-3 mt-8 flex flex-wrap items-center gap-3">
            <h2 className="text-base font-normal text-gray-900 dark:text-white">
              Diagnostic history
            </h2>
            {/* The filter is the server's (D27), so it reaches the whole
                history rather than the page in view: the rows, the pager's
                total and its page count move together. No count in the label —
                with archived rows excluded from the page the screen has not
                been told how many there are, and it is not the page's business
                anyway now that the filter is not scoped to it.

                Always offered, never conditional on the page holding an
                archived row: hiding them is precisely what makes them
                invisible, so a toggle that appeared only once one was on
                screen could never be used to bring them back. */}
            <Button
              aria-pressed={showArchived}
              className="h-auto p-0 text-xs font-normal text-gray-500 underline-offset-2 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              onClick={() => setShowArchived(!showArchived)}
              type="button"
              variant="link"
            >
              {showArchived ? "Hide archived" : "Show archived"}
            </Button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {showArchived
                ? "Archived diagnostics are counted in this history."
                : "Archived diagnostics are left out of this history and its count."}
            </span>
          </div>

          <DataTable<CompanyAttemptDetail>
            columns={columns}
            data={attempts}
            expandable
            loading={loading}
            noDataMessage={
              // A history that is wholly archived is not an empty one. It
              // reaches here as no rows at `totalCount: 0` — the same shape a
              // company with nothing at all would produce — but `company` is
              // populated, which is the API's signal that this company *has*
              // been diagnosed (the never-diagnosed case is handled above and
              // never reaches this table). With archived rows hidden, that
              // combination can only mean every one of them is archived.
              !showArchived && totalCount === 0
                ? 'Every diagnostic for this company is archived. Use "Show archived" to see them.'
                : "No diagnostics yet. Use New Diagnostic to start one."
            }
            onPageChange={handlePageChange}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            renderExpandedRow={(attempt) => (
              <AttemptBreakdown attempt={attempt} />
            )}
            rowKeyExtractor={(attempt) => attempt.id}
            // From `meta`, not the row count: the rows are one page.
            totalCount={totalCount}
          />
        </>
      )}

      <NewDiagnosticDialog
        companyId={companyId}
        onClose={() => setNewOpen(false)}
        open={newOpen}
      />

      <ConfirmationDialog
        cancelMessage="Cancel"
        confirmMessage={pendingArchive?.isArchived ? "Restore" : "Archive"}
        dialogInstructionFinalQuestion={
          pendingArchive?.isArchived
            ? "Restore this diagnostic?"
            : "Archive this diagnostic?"
        }
        dialogInstructionItems={
          pendingArchive?.isArchived
            ? ["It reappears in the default history view."]
            : [
                "Its answers and edit history are kept.",
                "It drops out of the default history view.",
                "It can be restored at any time.",
              ]
        }
        dialogInstructionTitle={
          pendingArchive?.isArchived
            ? "Restoring is immediate:"
            : "Archiving never deletes:"
        }
        dialogTitleContent={
          pendingArchive?.isArchived
            ? "Restore diagnostic"
            : "Archive diagnostic"
        }
        isOpen={pendingArchive !== null}
        onClose={() => setPendingArchive(null)}
        onConfirm={() => {
          if (pendingArchive) {
            setArchived(pendingArchive.id, !pendingArchive.isArchived);
          }
          setPendingArchive(null);
        }}
      />
    </MainPageWrapper>
  );
};

const statusLabel = (attempt: CompanyAttemptDetail): string => {
  if (attempt.isArchived) return "Archived";
  if (attempt.status === "in_progress") return "In progress";
  return attempt.finalOutcome === "ready" ? "Ready" : "Not ready";
};

const AttemptBreakdown: React.FC<{ attempt: CompanyAttemptDetail }> = ({
  attempt,
}) => {
  const sectionsById = new Map(attempt.version.sections.map((s) => [s.id, s]));
  const stage = displayStage(attempt);
  // Not `result.weightedTotal`, which is `secondaryScore ?? 0` and so prints a
  // fabricated 0.00 over an attempt where nothing scoreable was answered —
  // the reading `types/diagnostic.ts` forbids. The caption follows the value:
  // a gated instrument's headline is its secondary score, as the PDF says.
  const score = headlineScore(
    attempt.result,
    attempt.version.sections.filter((s) => s.isGate).map((s) => s.id),
  );

  return (
    <div className="space-y-4 p-4">
      {attempt.status === "complete" ? (
        <div className="flex flex-wrap items-center gap-3 rounded border border-gray-200 bg-white p-3 text-sm text-gray-900 dark:border-slate-700 dark:bg-slate-800 dark:text-white">
          <span className="text-gray-500 dark:text-gray-400">
            {attempt.result.gate ? "Secondary score" : "Weighted total"}
          </span>
          <span className="tabular-nums">
            {score == null ? "—" : score.toFixed(2)}
          </span>
          <span className="text-gray-500 dark:text-gray-400">·</span>
          <span className="text-gray-500 dark:text-gray-400">Stage</span>
          {stage ? (
            <Badge variant="grey">{stage}</Badge>
          ) : (
            <span className="text-gray-500 dark:text-gray-400">
              no stage placed
            </span>
          )}
        </div>
      ) : (
        <div className="rounded border border-gray-200 bg-white p-3 text-sm text-gray-500 dark:border-slate-700 dark:bg-slate-800 dark:text-gray-400">
          Score not yet final — this diagnostic is still in progress.
        </div>
      )}

      {attempt.result.perSection.length > 0 && (
        <div>
          <div className="mb-1.5 text-xs text-gray-500 dark:text-gray-400">
            Per-section
          </div>
          <div className="overflow-x-auto rounded border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="p-2 font-normal">Section</th>
                  <th className="p-2 font-normal">Avg</th>
                  <th className="p-2 font-normal">Weight</th>
                </tr>
              </thead>
              <tbody>
                {attempt.result.perSection.map((ps) => (
                  <tr
                    className="border-t border-gray-200 dark:border-slate-700"
                    key={ps.sectionId}
                  >
                    <td className="p-2 text-gray-900 dark:text-white">
                      {ps.name}
                    </td>
                    <td className="p-2 tabular-nums text-gray-500 dark:text-gray-400">
                      {ps.rawAvg !== null ? ps.rawAvg.toFixed(2) : "—"}
                    </td>
                    <td className="p-2 text-gray-500 dark:text-gray-400">
                      {sectionsById.get(ps.sectionId)?.isScored
                        ? `${ps.weight}%`
                        : "informational"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {attempt.coachNotes && (
        <div>
          <div className="mb-1.5 text-xs text-gray-500 dark:text-gray-400">
            Coach notes
          </div>
          <div className="whitespace-pre-wrap rounded border border-gray-200 bg-white p-3 text-sm text-gray-900 dark:border-slate-700 dark:bg-slate-800 dark:text-white">
            {attempt.coachNotes}
          </div>
        </div>
      )}

      {attempt.editCount > 0 && <AuditTrail attemptId={attempt.id} />}
    </div>
  );
};

export default CompanyDetail;
